/**
 * The idea Form core: the Draft the add/edit idea modals hold, its entry
 * states, its transitions, its validation and the payload builders where it
 * crosses into wire format. Pure — the modals render it and wire it to the
 * mutations, nothing more.
 */

import { DEFAULT_EVENT_CATEGORY } from '@/constants/eventCategories'
import { compactLinks, linkUrls } from '@lib/links'
import {
	emptyPlace,
	nameAfterPick,
	placeFromLocation,
	placeToPayload,
	PlaceDraft,
} from '@lib/place'
import { EventCategory, PostIdeaType, PutIdeaType } from '@orbit/api-types'
import { Idea } from './types'

export interface IdeaDraft {
	name: string
	description: string
	category: EventCategory | undefined
	place: PlaceDraft
	/** One row per link, blanks included — blank rows are dropped on the way out. */
	links: string[]
}

/** A fresh idea, optionally on the category the grid is filtered to. */
export const emptyIdeaDraft = (category?: EventCategory): IdeaDraft => ({
	name: '',
	description: '',
	category,
	place: emptyPlace(),
	links: [],
})

export const ideaDraftFrom = (idea: Idea): IdeaDraft => ({
	name: idea.name || '',
	description: idea.description || '',
	category: idea.category || undefined,
	place: placeFromLocation(idea.location),
	links: linkUrls(idea.links),
})

/**
 * Location text typed by hand. An idea keeps the place it had picked: the box
 * is a search field here, and narrowing the search does not retract the pick.
 */
export const setLocationText = (draft: IdeaDraft, text: string): IdeaDraft => ({
	...draft,
	place: { ...draft.place, text },
})

/** A place picked from the search: a Search result, a Trip place or a Suggestion. */
export const pickPlace = (draft: IdeaDraft, place: PlaceDraft): IdeaDraft => ({
	...draft,
	name: nameAfterPick(draft.name, draft.place, place),
	place,
})

/** An idea is submittable once it is named; everything else is optional. */
export const isIdeaDraftValid = (draft: IdeaDraft): boolean => draft.name.trim() !== ''

export const toCreatePayload = (draft: IdeaDraft, tripId: number): PostIdeaType => ({
	name: draft.name.trim(),
	description: draft.description.trim() || null,
	category: draft.category || DEFAULT_EVENT_CATEGORY,
	location: placeToPayload(draft.place) ?? null,
	links: compactLinks(draft.links),
	tags: [],
	tripId,
	userId: null,
})

/** The idea PUT replaces every editable field, so a field cleared in the Draft goes as null. */
export const toUpdatePayload = (draft: IdeaDraft): PutIdeaType => ({
	name: draft.name.trim(),
	description: draft.description.trim() || null,
	category: draft.category || null,
	location: placeToPayload(draft.place) ?? null,
	links: compactLinks(draft.links),
})
